import generateBreadcrumbs from 'molecules/Breadcrumbs/utils/generateBreadcrumbs';

import { trailingSlash } from 'utils/strings';

const SITE = 'https://prismatic.io';

interface BreadcrumbListItem {
  '@type': 'ListItem';
  position: number;
  name: string;
  item: string;
}

interface BreadcrumbListSchema {
  '@context': 'https://schema.org';
  '@type': 'BreadcrumbList';
  itemListElement: BreadcrumbListItem[];
}

/**
 * Builds BreadcrumbList structured data for a page path, mirroring the visual
 * breadcrumb trail (Home + each path segment). Returns null for the homepage
 * and any path with no segments, where a breadcrumb makes no sense.
 *
 * Generated here (not in the client-only Breadcrumbs molecule) so the JSON-LD
 * is emitted from the SSR'd <Head> on every page, the same reason the
 * Organization schema lives in code. Replaces the prior coverage where only
 * /dev-hub/ carried a BreadcrumbList.
 */
export const getBreadcrumbSchema = (path?: string | null): BreadcrumbListSchema | null => {
  const cleanPath = (path || '').split('?')[0];
  const segments = generateBreadcrumbs(cleanPath);

  if (!segments.length) {
    return null;
  }

  const home: BreadcrumbListItem = {
    '@type': 'ListItem',
    position: 1,
    name: 'Home',
    item: trailingSlash(SITE),
  };

  const items = segments.map<BreadcrumbListItem>(({ label, link }, index) => ({
    '@type': 'ListItem',
    position: index + 2,
    name: label,
    // generateBreadcrumbs returns site-relative links (e.g. /platform/),
    // absolutize them and normalize the trailing slash for canonical parity.
    item: trailingSlash(`${SITE}${link.split('?')[0]}`),
  }));

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [home, ...items],
  };
};
